import { useEffect, useRef, useState } from 'react'
import { cn } from '#app/utils/misc.tsx'

// Interactive gradient blob animation - uses CSS animations with mouse interactivity
export function InteractiveGradientBlob({
	containerClassName,
	// Brand colors: orange (#f6921e) and red/pink (#eb008b)
	backgroundColor = '#231a1a',
	size = '80%',
}: {
	containerClassName?: string
	backgroundColor?: string
	size?: string
}) {
	const interactiveRef = useRef<HTMLDivElement>(null)
	const [curX, setCurX] = useState(0)
	const [curY, setCurY] = useState(0)
	const [tgX, setTgX] = useState(0)
	const [tgY, setTgY] = useState(0)

	// Set CSS custom properties
	useEffect(() => {
		// Brand colors
		document.body.style.setProperty('--gradient-background-start', backgroundColor)
		document.body.style.setProperty('--gradient-background-end', backgroundColor)
		document.body.style.setProperty('--first-color', '246, 146, 30') // Orange
		document.body.style.setProperty('--second-color', '235, 0, 139') // Pink
		document.body.style.setProperty('--third-color', '246, 146, 30') // Orange
		document.body.style.setProperty('--fourth-color', '235, 0, 139') // Pink
		document.body.style.setProperty('--fifth-color', '246, 146, 30') // Orange
		document.body.style.setProperty('--pointer-color', '235, 0, 139') // Pink for mouse
		document.body.style.setProperty('--size', size)
		document.body.style.setProperty('--blending-value', 'hard-light')
	}, [backgroundColor, size])

	// Smooth mouse following animation
	useEffect(() => {
		function move() {
			if (!interactiveRef.current) {
				return
			}
			setCurX((prev) => prev + (tgX - prev) / 20)
			setCurY((prev) => prev + (tgY - prev) / 20)
			interactiveRef.current.style.transform = `translate(${Math.round(curX)}px, ${Math.round(curY)}px)`
		}

		move()
	}, [tgX, tgY, curX, curY])

	const handleMouseMove = (event: React.MouseEvent<HTMLDivElement>) => {
		if (interactiveRef.current) {
			const rect = interactiveRef.current.getBoundingClientRect()
			setTgX(event.clientX - rect.left)
			setTgY(event.clientY - rect.top)
		}
	}

	return (
		<div
			className={cn(
				'absolute inset-0 overflow-hidden',
				containerClassName,
			)}
			style={{ backgroundColor }}
		>
			{/* Animated gradients container — see notes in
			    interactive-gradient-circle.tsx for the goo strategy. */}
			<div
				className={cn(
					'gradients-container h-full w-full',
					'[filter:blur(40px)] [transform:translateZ(0)] [will-change:filter]',
				)}
				onMouseMove={handleMouseMove}
			>
				{/* First blob - orange, vertical movement */}
				<div
					className={cn(
						'absolute [background:radial-gradient(circle_at_center,_rgba(var(--first-color),_0.8)_0,_rgba(var(--first-color),_0)_50%)_no-repeat]',
						'[mix-blend-mode:var(--blending-value)] w-[var(--size)] h-[var(--size)] top-[calc(50%-var(--size)/2)] left-[calc(50%-var(--size)/2)]',
						'[transform-origin:center_center]',
						'animate-first',
						'opacity-100',
					)}
				/>

				{/* Second blob - pink, circular movement */}
				<div
					className={cn(
						'absolute [background:radial-gradient(circle_at_center,_rgba(var(--second-color),_0.8)_0,_rgba(var(--second-color),_0)_50%)_no-repeat]',
						'[mix-blend-mode:var(--blending-value)] w-[var(--size)] h-[var(--size)] top-[calc(50%-var(--size)/2)] left-[calc(50%-var(--size)/2)]',
						'[transform-origin:calc(50%-400px)]',
						'animate-second',
						'opacity-100',
					)}
				/>

				{/* Third blob - orange, circular movement */}
				<div
					className={cn(
						'absolute [background:radial-gradient(circle_at_center,_rgba(var(--third-color),_0.8)_0,_rgba(var(--third-color),_0)_50%)_no-repeat]',
						'[mix-blend-mode:var(--blending-value)] w-[var(--size)] h-[var(--size)] top-[calc(50%-var(--size)/2)] left-[calc(50%-var(--size)/2)]',
						'[transform-origin:calc(50%+400px)]',
						'animate-third',
						'opacity-100',
					)}
				/>

				{/* Fourth blob - pink, horizontal movement */}
				<div
					className={cn(
						'absolute [background:radial-gradient(circle_at_center,_rgba(var(--fourth-color),_0.8)_0,_rgba(var(--fourth-color),_0)_50%)_no-repeat]',
						'[mix-blend-mode:var(--blending-value)] w-[var(--size)] h-[var(--size)] top-[calc(50%-var(--size)/2)] left-[calc(50%-var(--size)/2)]',
						'[transform-origin:calc(50%-200px)]',
						'animate-fourth',
						'opacity-70',
					)}
				/>

				{/* Fifth blob - orange, circular movement */}
				<div
					className={cn(
						'absolute [background:radial-gradient(circle_at_center,_rgba(var(--fifth-color),_0.8)_0,_rgba(var(--fifth-color),_0)_50%)_no-repeat]',
						'[mix-blend-mode:var(--blending-value)] w-[var(--size)] h-[var(--size)] top-[calc(50%-var(--size)/2)] left-[calc(50%-var(--size)/2)]',
						'[transform-origin:calc(50%-800px)_calc(50%+800px)]',
						'animate-fifth',
						'opacity-100',
					)}
				/>

				{/* Interactive pointer-following blob */}
				<div
					ref={interactiveRef}
					className={cn(
						'absolute [background:radial-gradient(circle_at_center,_rgba(var(--pointer-color),_0.8)_0,_rgba(var(--pointer-color),_0)_50%)_no-repeat]',
						'[mix-blend-mode:var(--blending-value)] w-full h-full -top-1/2 -left-1/2',
						'opacity-70',
					)}
				/>
			</div>
		</div>
	)
}
